import { createContext, useContext, useState, useEffect } from 'react';
import { setLogoutHandler } from '../api/interceptor';
import { useLocation } from '../hooks/useLocation';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      return null;
    }
  });

  const [tokens, setTokens] = useState(() => {
    try {
      const savedTokens = localStorage.getItem('tokens');
      return savedTokens ? JSON.parse(savedTokens) : null;
    } catch {
      return null;
    }
  });

  const { logAction } = useLocation();

  const login = (userData, tokenData) => {
    try {
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('tokens', JSON.stringify(tokenData));
      setUser(userData);
      setTokens(tokenData);
      
      // Log login action with location
      logAction('login').catch(error => {
        console.warn('Failed to log login location:', error);
        // Continue even if location logging fails
      });
    } catch (error) {
      console.error('Storage error:', error);
      setUser(userData);
      setTokens(tokenData);
    }
  };

  const logout = () => {
    console.log('Logging out user');
    // Clear localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('tokens');
    localStorage.removeItem('authToken'); // Also remove authToken if it exists
    
    // Clear sessionStorage
    sessionStorage.clear();
    
    // Reset state
    setUser(null);
    setTokens(null);
    
    // Add cache-busting for Safari/iOS
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    // Store current path for potential "session expired" message
    const currentPath = window.location.pathname;
    if (currentPath !== '/login') {
      sessionStorage.setItem('sessionExpired', 'true');
    }
    
    // Implement a small delay to ensure all state is cleared before redirect
    setTimeout(() => {
      if (isSafari || isIOS) {
        // Force page reload with cache-busting parameter
        window.location.href = '/login?nocache=' + new Date().getTime();
      } else {
        // Standard redirect for other browsers
        window.location.href = '/login';
      }
    }, 100);
  };

  // Set up logout handler for the API interceptor
  useEffect(() => {
    console.log('Setting up logout handler');
    setLogoutHandler(logout);
    
    // Debug check for tokens in localStorage on component load
    try {
      const tokensString = localStorage.getItem('tokens');
      if (tokensString) {
        console.log('Tokens found in localStorage:', JSON.parse(tokensString));
      } else {
        console.log('No tokens found in localStorage');
      }
    } catch (e) {
      console.error('Error checking tokens in localStorage:', e);
    }
  }, []);

  const isAuthenticated = Boolean(user && tokens?.access?.token);
  
  // Debug logging to help track authentication state
  useEffect(() => {
    console.log('Authentication state updated:', { 
      isAuthenticated, 
      hasUser: !!user, 
      hasToken: !!tokens?.access?.token 
    });
  }, [isAuthenticated, user, tokens]);

  const value = {
    user,
    tokens,
    login,
    logout,
    isAuthenticated
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};